import React, {useEffect, useCallback, useRef, useState, useMemo} from 'react';
import {ScratchBlocks, ScratchExtensions} from '@hetao/scratch-core';
import Box from '../box/box';
import styles from './white-list.css';
import virtualBlockInfo, {categoryObj} from './virtual-block-info';
import {whiteListToolboxXMLSync} from '../../lib/whiteListToolboxXML';
import {INCLUDED_EXTENSIONS} from '../../lib/whiteListToolboxXML';
const {DisguisedBlockly} = ScratchExtensions;

const workspaceOptions = {
    zoom: {
        controls: false,
        wheel: true,
        startScale: 0.7
    },
    comments: false,
    scrollbars: true,
    collapse: false,
    rtl: false,
    media: ''
};

const BLOCK_OPACITY = 0.5;

const categoryRenderList = [
    {
        color: '#4C97FF',
        id: ScratchBlocks.Categories.motion,
        name: ''
    },
    {
        color: '#9966FF',
        id: ScratchBlocks.Categories.looks,
        name: ''
    },
    {
        color: '#CF63CF',
        id: 'sound',
        name: ''
    },
    {
        color: '#E1BA3F',
        id: ScratchBlocks.Categories.event,
        name: ''
    },
    {
        color: '#FFAB19',
        id: ScratchBlocks.Categories.control,
        name: ''
    },
    {
        color: '#5CB1D6',
        id: ScratchBlocks.Categories.sensing,
        name: ''
    },
    {
        color: '#59C059',
        id: ScratchBlocks.Categories.operators,
        name: ''
    },
    {
        color: '#FF8C1A',
        id: 'data',
        name: '变量'
    },
    {
        color: '#FF6680',
        id: 'procedures',
        name: '自定义'
    }
];
const flexCategoryRenderList = [
    {
        color: '#0FBD8C',
        id: 'text2speech',
        name: '文字朗读'
    },
    {
        color: '#ea7074',
        id: 'speech2text',
        name: '语音识别'
    },
    {
        color: '#e98631',
        id: 'translate',
        name: '翻译'
    },
    {
        color: '#9A68FD',
        id: 'doodleSense',
        name: '涂鸦识别'
    },
    {
        color: '#9966CC',
        id: 'txt2img',
        name: '生成图片'
    },
    {
        color: '#32CD32',
        id: 'aichat',
        name: '自然对话'
    },
    {
        color: '#4A90E4',
        id: 'ailab',
        name: 'ai实验室'
    }

];
const NO_BLOCKS_CATEGORIES_INFO = {
    variables: {
        initialBlockXML: `<block type="data_variable"><field name="VARIABLE" variabletype="">我的变量</field></block>`
    },
    myBlocks: {
        initialBlockXML: '<block type="procedures_call"><mutation proccode="自定义代码块" argumentids="[]" warp="false"></mutation></block>'
    }
};
 
const CATEGORY_BLACK_LIST = ['pen', 'music', 'magicblocks'];
const domStringToDom = domString => {
    const fragmentEle = document.createElement('category');
    fragmentEle.innerHTML = domString;
    return fragmentEle.firstChild;
};

categoryRenderList.forEach(categoryInfo => {
    if (!categoryInfo.name) {
        categoryInfo.name = categoryObj[categoryInfo.id].name;
    }
});

const useWorkspace = (blocksRef, workspaceConfig) => {
    const [workspace, setWorkspace] = useState(null);
    useEffect(() => {
        const oldDefaultToolbox = ScratchBlocks.Blocks.defaultToolbox;
        ScratchBlocks.Blocks.defaultToolbox = null;
        const workspace = ScratchBlocks.inject(blocksRef.current, workspaceConfig);
        ScratchBlocks.Blocks.defaultToolbox = oldDefaultToolbox;
        setWorkspace(workspace);
    }, []);
    return workspace;
};

const useXMLCategories = toolboxXML => useMemo(() => {
    const languageTree = ScratchBlocks.Xml.textToDom(toolboxXML);
    const categories = Array.from(languageTree.getElementsByTagName('category'));
    const contents_ = [];
    let lastCategoryName = '';
    for (let i = 0; i < categories.length; i++) {
        const category1 = categories[i];
        const categoryName = category1.getAttribute('id');
        if (CATEGORY_BLACK_LIST.includes(categoryName)) {
            continue;
        }
        if (categoryName !== lastCategoryName) {
            lastCategoryName = categoryName;
            const blocksForCategory = [];
            if (Object.keys(NO_BLOCKS_CATEGORIES_INFO).includes(categoryName)) {
                const dom = domStringToDom(NO_BLOCKS_CATEGORIES_INFO[categoryName].initialBlockXML);
                blocksForCategory.push(dom);
            }
            contents_.push(blocksForCategory);
        }
        const blocks = category1.childNodes;
        for (let j = 0; j < blocks.length; j++) {
            const block = blocks[j];
            if (
                block.nodeName.toUpperCase() !== 'BLOCK' &&
                    block.nodeName.toUpperCase() !== 'SEP'
            ) {
                continue;
            }
            contents_[contents_.length - 1].push(block);
        }
    }
    return contents_;
}, [toolboxXML]);

const getBlockOuterHTML = (blockXML, blockType) => {
    const blocklyDyInputBlocks = ScratchBlocks.dynamicInputBlocks;
    if (blocklyDyInputBlocks.includes(blockType)) {
        let blockInfo = null;
        Object.keys(virtualBlockInfo).forEach(blockKey => {
            const categoryBlocks = virtualBlockInfo[blockKey];
            const block = categoryBlocks.find(block => block.opcode === blockType);
            if (block) {
                blockInfo = block;
            }
        });
        return blockInfo.value;
    }
    return blockXML.outerHTML;
};

const useWhitelistSaver = (vm, categoryObj) => useCallback((blockXmlContents, blockSeparatorMap, workspace) => {
    const blocksMap = {};
    for (let i = 0; i < blockXmlContents.length; i++) {
        const block = blockXmlContents[i];
        if (block.opacity_ === BLOCK_OPACITY) {
            continue;
        }
        const blockType = block.getAttribute('type');
        let category = blockType.split('_')[0];
        if (category === 'event') {
            category = 'events';
        }
        if (category === 'operator') {
            category = 'operators';
        }
        if (category === 'data') {
            category = 'variables';
        }
        if (category === 'procedures') {
            category = 'myBlocks';
        }
        if (blocksMap[category] === undefined) {
            blocksMap[category] = [`${categoryObj[category].xml}`, `${categoryObj[category].xmlclose}`];
        }
        // Does not add any blocks for NO_BLOCKS_CATEGORIES
        if (Object.keys(NO_BLOCKS_CATEGORIES_INFO).includes(category)) {
            continue;
        }
        // 添加block前面的separatorNode
        if (blockSeparatorMap.current[blockType]) {
            const blockOuterHTML =
                blocksMap[category].splice(blocksMap[category].length - 1, 0, getBlockOuterHTML(block, blockType), blockSeparatorMap.current[blockType]);
        } else {
            blocksMap[category].splice(blocksMap[category].length - 1, 0, getBlockOuterHTML(block, blockType));
        }
    }

    const lowOpacityBlocks = workspace.getAllBlocks().filter(block => block.opacity_ === BLOCK_OPACITY);
    for (const ext of INCLUDED_EXTENSIONS) {
        const extCategory = ext;
        const hasLowOpacityBlock = lowOpacityBlocks.some(block => block.type.startsWith(extCategory));
        if (hasLowOpacityBlock) {
            // 被要求能使用白名单的拓展模块特殊处理、如果该拓展模块被设置全部不要显示时，blocksMap[extCategory]会是空的，处理成放个空的category占位
            if (!blocksMap.hasOwnProperty(extCategory)) {
                blocksMap[extCategory] = [`${categoryObj[extCategory].xml}`,
                    `${categoryObj[extCategory].xmlclose}`];
            }
        } else {
            // 如果拓展是默认没有做过任何配置，相当于未做过配置，处理成把blocksMap[extCategory]删除，这样逻辑比较顺，不要误配置意外影响到不支持拓展白名单的项目而不知，如果你配了证明是有所了解的
            // eslint-disable-next-line no-lonely-if
            if (blocksMap.hasOwnProperty(extCategory)) {
                delete blocksMap[extCategory];
            }
        }

    }

    for (const item in blocksMap) {
        blocksMap[item] = blocksMap[item].join('');
    }
        
    // sorting
    vm.setToolbox(JSON.stringify({
        motion: blocksMap.motion,
        looks: blocksMap.looks,
        sound: blocksMap.sound,
        events: blocksMap.events,
        control: blocksMap.control,
        sensing: blocksMap.sensing,
        operators: blocksMap.operators,
        variables: blocksMap.variables,
        myBlocks: blocksMap.myBlocks,
        ...(INCLUDED_EXTENSIONS.reduce((acc, ext) => {
            if (!blocksMap[ext]) {
                return acc;
            }
            acc[ext] = blocksMap[ext];
            return acc;
        }, {}))
    }));
}, []);

const useWhiteToolboxTreeMap = vm => {
    const whiteListCategoryXml = vm.getToolbox();
    return useMemo(() => {
        if (!whiteListCategoryXml) {
            return new Document();
        }
        const toolboxXML = whiteListToolboxXMLSync(vm.editingTarget.isStage, vm.editingTarget.id, '', whiteListCategoryXml);
        const tree = ScratchBlocks.Xml.textToDom(toolboxXML.xml);
        const treeMap = {};
        const whiteToolboxList = Array.from(tree.getElementsByTagName('block'));
        const categoryList = Array.from(tree.getElementsByTagName('category'));
        categoryList.forEach(category => {
            const categoryName = category.getAttribute('id');
            const hasIntrinsicCategories = Object.keys(NO_BLOCKS_CATEGORIES_INFO).includes(categoryName);
            if (hasIntrinsicCategories) {
                const initialBlock = domStringToDom(NO_BLOCKS_CATEGORIES_INFO[categoryName].initialBlockXML);
                whiteToolboxList.push(initialBlock);
            }
        });
        for (let i = 0; i < whiteToolboxList.length; i++) {
            const whiteToolboxListElement = whiteToolboxList[i];
            if (whiteToolboxListElement.tagName.toUpperCase() !== 'BLOCK') {
                continue;
            }
            const blockType = whiteToolboxListElement.getAttribute('type');
            treeMap[blockType] = whiteToolboxListElement;
        }
        return treeMap;
    }, [whiteListCategoryXml, vm]);
};

const RealBlockList = props => {
    const {toolboxXML, media, onRequestClose, vm} = props;
    workspaceOptions.media = media;
    const blocksRef = useRef(null);
    const cachedBlocksRef = useRef([]);
    const blockSeparatorMap = useRef({});
    const [allBlocks, setAllBlocks] = useState({});
    const workspace = useWorkspace(blocksRef, workspaceOptions);
    const workspaceListener = useCallback(event => {
        // add select effects
        if (event.type === 'ui' && event.element === 'click') {
            const selectedBlock = workspace.blockDB_[event.blockId];
            if (selectedBlock.opacity_ === BLOCK_OPACITY) {
                selectedBlock.setOpacity(1);
            } else {
                selectedBlock.setOpacity(BLOCK_OPACITY);
            }
        }
    }, [workspace]);
    const calcNewBlockPos = useCallback(cachedBlocks => {
        const x = 0; let y = 0;
        if (cachedBlocks.length) {
            y = cachedBlocks.reduce((preBlock, nextBlock) => preBlock + nextBlock.height + 15, 0);
        }
        return {x, y};
    }, []);
    const xmlCategories = useXMLCategories(toolboxXML);
    const saveWhitelist = useWhitelistSaver(vm, categoryObj);
    const whiteToolboxTreeMap = useWhiteToolboxTreeMap(vm);

    useEffect(() => {
        if (workspace) {
            window.ws = workspace;
            workspace.addChangeListener(workspaceListener);
            // saved whiteListCategoryXml
            const savedWhiteListCategoryXml = vm.getToolbox();
            const categoryObj = savedWhiteListCategoryXml ? eval(`(${savedWhiteListCategoryXml})`) : {};
            const isAllBlocksOfExtCategoryVisible = category => {
                if (!categoryObj.hasOwnProperty(category)) return true;
                return false;
            };
            // intrinsic category xml
            for (let i = 0, category; (category = xmlCategories[i]); i++) {
                cachedBlocksRef.current = [];
                for (let j = 0; j < category.length; j++) {
                    let blockXML = category[j];
                    const curBlockType = blockXML.getAttribute('type');
                    if (blockXML.tagName.toUpperCase() === 'SEP') {
                        blockSeparatorMap.current[blockXML.previousElementSibling.getAttribute('type')] = blockXML.outerHTML;
                        continue;
                    }
                    if (curBlockType.endsWith(DisguisedBlockly.DISGUISED_BLOCK_OPCODE_SUFFIX)) {
                        continue;
                    }
                    const definedInWhiteListBlock = whiteToolboxTreeMap[curBlockType];
                    // setting initial value
                    if (savedWhiteListCategoryXml && definedInWhiteListBlock) {
                        blockXML = definedInWhiteListBlock;
                    }
                    const newBlock = ScratchBlocks.Xml.domToBlock(blockXML, workspace);
                    newBlock.setMovable(false);
                    newBlock.setEditable(true);
                    newBlock.setDeletable(false);
                    newBlock.setMovable(false);
                    newBlock.contextMenu = false;
                    newBlock.initSvg();
                    // setting status of selected block
                    if (savedWhiteListCategoryXml && !definedInWhiteListBlock) {
                        newBlock.setOpacity(BLOCK_OPACITY);
                    }
                    const newPos = calcNewBlockPos(cachedBlocksRef.current);
                    newBlock.moveBy(400 * i, newPos.y);
                    newBlock.render();
                    // const category_ = newBlock.getCategory();
                    let category_ = newBlock.type.split('_')[0];
                    // 因为拓展模块不配就默认不会加入xml中需要是展示所以这种情况要高亮
                    if (INCLUDED_EXTENSIONS.includes(category_) && !definedInWhiteListBlock && isAllBlocksOfExtCategoryVisible(category_)) {
                        newBlock.setOpacity(1);
                    }
                    if (category_ === 'event' || category_ === 'operator' || category_ === 'sound') {
                        category_ = `${category_}s`;
                    }
                    if (!allBlocks[category_]) {
                        allBlocks[category_] = [];
                    }
                    console.log('category_', category_);
                    console.log('allBlocks', allBlocks);
                    allBlocks[category_].push(newBlock);
                    cachedBlocksRef.current.push(newBlock);
                }
            }
            setAllBlocks({...allBlocks});
        }
        return () => {
            if (workspace) {
                workspace.removeChangeListener(workspaceListener);
            }
        };
    }, [workspace, xmlCategories]);

    const handleSaveWhitelist = useCallback(() => {
        const workspaceXML = ScratchBlocks.Xml.workspaceToDom(workspace);
        const contents_ = workspaceXML.childNodes;
        // 包含separator和block元素
        const blockXMLList = [];
        for (let i = 0; i < contents_.length; i++) {
            const content = contents_[i];
            if (content.tagName.toUpperCase() !== 'BLOCK') {
                continue;
            }
            const blockId = content.getAttribute('id');
            const block = workspace.getBlockById(blockId);
            if (block.opacity_ === BLOCK_OPACITY) {
                continue;
            }
            // remove useless attributes
            content.removeAttribute('id');
            content.removeAttribute('deletable');
            content.removeAttribute('movable');
            content.removeAttribute('x');
            content.removeAttribute('y');
            const shadows = Array.from(content.getElementsByTagName('shadow'));
            const fields = Array.from(content.getElementsByTagName('field'));
            const nodes = shadows.concat(fields);
            for (let j = 0; j < nodes.length; j++) {
                const node = nodes[j];
                node.removeAttribute('id');
            }
            blockXMLList.push(content);
        }
        saveWhitelist(blockXMLList, blockSeparatorMap, workspace);
        onRequestClose();
    }, [workspace]);

    const handleClickAllCategory = useCallback((category, isSelected) => {
        const categoryId = category.id === 'sound' ? 'sounds' : category.id;
        const categoryBlockList = allBlocks[categoryId];
        for (let i = 0; i < categoryBlockList.length; i++) {
            const block_ = categoryBlockList[i];
            const opacity = isSelected ? 1 : BLOCK_OPACITY;
            block_.setOpacity(opacity);
        }
        setAllBlocks({...allBlocks});
    }, [workspace]);

    const isSelectedAllCategory = useCallback(categoryInfo => {
        const categoryId = categoryInfo.id === 'sound' ? 'sounds' : categoryInfo.id;
        if (allBlocks[categoryId]) {
            return allBlocks[categoryId].every(block => block.opacity_ === BLOCK_OPACITY);
        }
        return false;
    }, []);

    const visibleExtensionCategoryRenderList = useMemo(() => {
        const allBlockList = allBlocks;
        const categories = Object.keys(allBlockList);
        const filteredCategoryRenderList = flexCategoryRenderList.filter(categoryInfo => categories.includes(categoryInfo.id));
        console.log('filteredCategoryRenderList', filteredCategoryRenderList);
        return filteredCategoryRenderList;
    }, [allBlocks]);


    return (<Box
        className={styles.whiteListContainer}
        justifyContent={'space-between'}
        direction={'column'}
    >
        <header className={styles.top}>
            <h3>{'白名单编辑'}</h3>
            <span>
                <button onClick={handleSaveWhitelist}>{'保存'}</button>
                <i onClick={onRequestClose}>{'取消'}</i>
            </span>
        </header>
        <div className={styles.categoryHeader}>
            {
                categoryRenderList.concat(visibleExtensionCategoryRenderList).map(categoryInfo => {
                    const isSelected = isSelectedAllCategory(categoryInfo);
                    return (<span
                        className={styles.categoryTag}
                        onClick={() => handleClickAllCategory(categoryInfo, isSelected)}
                        style={{background: categoryInfo.color}}
                    >
                        {`${isSelected ? '显示' : '隐藏'}${categoryInfo.name}类目`}
                    </span>);
                })
            }
        </div>
        <div
            className={styles.blocksEditor}
            ref={blocksRef}
        />
    </Box>);
};

export default RealBlockList;
